import React from 'react';
import { TodoTree, TodoItem } from '../types/todo';
import { getItemById } from '../utils/tree';
import { useTodoContext } from '../contexts/TodoContext';
import { LAYOUT } from '../utils/constants';

interface ArchivedTaskGroupProps {
  /** Array of archived task IDs to display */
  archivedTaskIds: string[];
  /** The tree structure for looking up task details */
  tree: TodoTree;
  /** Parent node ID for positioning */
  parentId: string;
  /** Calculated Y position for the group container */
  yPosition: number;
  /** Column index for horizontal positioning */
  columnIndex: number;
}

const ArchivedTaskGroup: React.FC<ArchivedTaskGroupProps> = ({
  archivedTaskIds,
  tree,
  parentId,
  yPosition,
  columnIndex,
}) => {
  const { unarchiveItem } = useTodoContext();

  // Don't render if no archived tasks
  if (archivedTaskIds.length === 0) {
    return null;
  }

  const handleTaskClick = (taskId: string) => {
    unarchiveItem(taskId);
  };

  const formatTaskName = (item: TodoItem): string => {
    // Truncate long names for compact display
    const maxLength = 25;
    if (item.name.length > maxLength) {
      return item.name.substring(0, maxLength) + '...';
    }
    return item.name;
  };

  const hasChildren = (item: TodoItem): boolean => {
    return item.children.length > 0;
  };

  // Calculate position for the archived group
  const calculatePosition = () => {
    const columnWidth = LAYOUT.NODE_WIDTH + LAYOUT.COLUMN_SPACING;
    const left = columnIndex * columnWidth + LAYOUT.TREE_PADDING;

    return {
      left: `${left}px`,
      top: `${yPosition}px`,
      position: 'absolute' as const
    };
  };

  return (
    <div
      id={`archived-group-${parentId}`}
      className="archived-task-group"
      style={calculatePosition()}
    >
      <div className="archived-group-header">
        📦 Archived ({archivedTaskIds.length})
      </div>
      <div className="archived-task-list">
        {archivedTaskIds.map(taskId => {
          const item = getItemById(tree, taskId);
          if (!item) return null;

          return (
            <div
              key={taskId}
              className="archived-task-item"
              onClick={() => handleTaskClick(taskId)}
              title={`Click to unarchive: ${item.name}${item.description ? '\n' + item.description : ''}`}
            >
              <span className="archived-task-icon">📦</span>
              <span className="archived-task-name">
                {formatTaskName(item)}
              </span>
              {hasChildren(item) && (
                <span className="archived-task-children-indicator" title="Has children">
                  [+]
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ArchivedTaskGroup;